import { createReducer } from "reduxsauce"
import actionCreator from "helpers/actionCreator"
import { first, get, filter, forEach, isNumber, omit, size } from "lodash"
import { post as apiPost } from "api"
import { Types as LoaderTypes } from "model/data"
import { LOADING, ERROR, OK, UNKNOWN } from "helpers/data-statuses"
import { takeLatest, call, select, put } from "redux-saga/effects"
import { Creators as notificationCreators } from "modules/notifications/model"
import { getViewType, getPortfolioId, getSelectedModelId } from "modules/portfolio-root/selectors/portfolios"
import { getBasePositionSet } from "selectors/base"
import { getForecast } from "./selectors/data"
import { getApiConstraints, getCurrentConstraints, getCurrentSettings, getSelectedCustomForecast, getForecastConstraint } from "./selectors/constraints"
import { getFrontierDate } from "modules/experiments/selectors/dates"
import { Types as ExperimentsTypes } from "modules/experiments/model"
import { getUserPreference } from "helpers/userPreferences"
import { getPreferenceKeyType, resetPreferencesItems } from "./helpers"
import { VIEW_TYPES } from "helpers/base"

export const { Types, Creators } = actionCreator("ompnt/modules/compareFrontier/", {
  frontierChart: [],
  frontierLoading: [],
  comparisonItemLoadingSuccess: [],
  setGroup: ["group"],
  frontierChartSuccess: ["result"],
  frontierChartError: [],
  chartLegendFocus: ["item"],
  setConstraints: {
    props: ["constraintsType", "constraints"],
    updatePreference: {
      getItems: (state, { constraintsType }) => [{
        ...getPreferenceKeyType(state, constraintsType),
        value: get(getCurrentConstraints(state), constraintsType),
      }],
    },
  },
  setSettings: {
    props: ["settings"],
    updatePreference: {
      getItems: (state) => [{
        type: "compareFrontierForecast",
        key: getPortfolioId(state),
        value: get(getCurrentConstraints(state), "settings"),
      }],
    },
  },
  removeFactor: {
    props: ["factorId", "contentSetId"],
    updatePreference: {
      getItems: (state) => [{
        ...getPreferenceKeyType(state, "exposure"),
        value: get(getCurrentConstraints(state), "exposure"),
      }],
    },
  },
  resetSettings: {
    props: [],
    updatePreference: {
      actionType: "delete",
      getItems: (state) => [...resetPreferencesItems(state)],
    },
  },
  toggleFrontierSettings: [],
  setConstraint: ["id", "value"],
  setSetting: ["id", "value"],
  removeActivatedFactor: ["factorId", "contentSetId"],
  setFactors: ["factorId", "contentSetId", "min", "max"],
})

const DEFAULT_STATE = {
  selectedGroup: null,
  focusedLegendItem: null,
  hoveredLegendItem: null,
  frontierChartData: [],
  status: UNKNOWN,
  reloadChart: false,
  comparisonItemsStatus: null,
  frontierSettingsPanelOpen: false,
  constraints: {
    settings: {
      horizon: 252,
    },
    general: {
      tradeSwaps: true,
    },
    exposure: [],
    securities: {},
  },
  activatedConstraints: {
    settings: {},
    general: {},
    exposure: {},
  },
}

function handleSetGroup(state, { group }) {
  return { ...state, selectedGroup: group }
}

function handleFrontierChartSuccess(state, { result }) {
  return {
    ...state,
    frontierChartData: result,
    status: OK,
    reloadChart: false,
  }
}

function handleComparisonItemLoading(state) {
  return { ...state, comparisonItemsStatus: LOADING }
}

function handleComparisonItemLoadingSuccess(state) {
  return { ...state, comparisonItemsStatus: OK }
}

function handleFrontierLoading(state) {
  return { ...state, status: LOADING }
}

function handleFrontierChartError(state) {
  return { ...state, error: "A technical error has happened. Please try again later.", status: ERROR }
}

function handleChartLegendFocus(state, { item }) {
  const newState = { hoveredLegendItem: item }

  if (item) {
    newState.focusedLegendItem = item
  }

  return { ...state, ...newState }
}

function handleLoadSuccess(state, action) {
  if (action.resource === "portfolio") {
    return { ...state, ...DEFAULT_STATE, portfolioType: action.data.type }
  }

  if (action.resource === "compareFrontierConstraintsPreferences") {
    const constraints = {
      settings: {
        horizon: 252,
      },
      general: {
        tradeSwaps: true,
      },
      exposure: [],
      securities: {},
    }

    const storedConstraints = getUserPreference(action.data, "compareFrontierConstraints")
    if (storedConstraints) {
      forEach(storedConstraints, (value, id) => {
        if (state.portfolioType === "LONG_SHORT" || (state.portfolioType !== "LONG_SHORT" && id !== "netLong")) {
          constraints.general[id] = isNumber(value) ? parseFloat(value) : value
        }
      })
    }

    const storedForecastSettings = getUserPreference(action.data, "compareFrontierForecast")

    if (storedForecastSettings) {
      forEach(storedForecastSettings, (value, id) => {
        constraints.settings[id] = isNumber(value) ? parseInt(value, 10) : value
      })
    }

    constraints.exposure = getUserPreference(action.data, "compareFrontierExposures", [])

    return { ...state, ...DEFAULT_STATE, constraints }
  }

  return state
}

function handleSetConstraints(state, { constraintsType, constraints }) {
  const newConstraints = { ...state.constraints, [constraintsType]: constraints }

  return {
    ...state,
    constraints: newConstraints,
    status: UNKNOWN,
    reloadChart: true,
  }
}

function handleRemoveFactor(state, { factorId, contentSetId }) {
  const newConstraints = { ...state.constraints, exposure: filter(state.constraints.exposure, factor => !(factor.id === factorId && factor.contentSetId === contentSetId)) }

  return {
    ...state,
    constraints: newConstraints,
    status: UNKNOWN,
    reloadChart: true,
  }
}

function handleResetSettings(state) {
  const newConstraints = {
    settings: {
      horizon: 252,
    },
    general: {
      tradeSwaps: true,
    },
    exposure: [],
    securities: {},
  }

  return {
    ...state,
    constraints: newConstraints,
    status: UNKNOWN,
    reloadChart: true,
    activatedConstraints: {
      settings: {},
      general: {},
      exposure: {},
    },
  }
}

function handleSetSettings(state, { settings }) {
  const newConstraints = { ...state.constraints, settings }

  return {
    ...state,
    constraints: newConstraints,
    status: UNKNOWN,
    reloadChart: true,
  }
}

function handleToggleFrontierSettings(state) {
  return { ...state, frontierSettingsPanelOpen: !state.frontierSettingsPanelOpen }
}

function handleSetReloadChart(state) {
  return { ...state, reloadChart: true }
}

function handleSetConstraint(state, { id, value }) {
  // [TODO] use immutable-helpers
  return { ...state,
    activatedConstraints: {
      ...state.activatedConstraints,
      general: {
        ...state.activatedConstraints.general,
        [id]: { value },
      },
    },
  }
}

function handleSetSetting(state, { id, value }) {
  return { ...state,
    activatedConstraints: {
      ...state.activatedConstraints,
      settings: {
        ...state.activatedConstraints.settings,
        [id]: { value },
      },
    },
  }
}

function handleSetFactors(state, { factorId, contentSetId, min, max }) {
  const id = contentSetId ? `${contentSetId}.${factorId}` : factorId
  return { ...state,
    activatedConstraints: { ...state.activatedConstraints, exposure: { ...state.activatedConstraints.exposure, [id]: { min, max, factorId, contentSetId } } } }
}

function handleRemoveActivatedFactor(state, { factorId, contentSetId }) {
  const id = contentSetId ? `${contentSetId}.${factorId}` : factorId
  return {
    ...state,
    activatedConstraints: {
      ...state.activatedConstraints,
      exposure: omit(state.activatedConstraints.exposure, id),
    },
  }
}

export default createReducer(DEFAULT_STATE, {
  [Types.FRONTIER_CHART_SUCCESS]: handleFrontierChartSuccess,
  [Types.FRONTIER_CHART_ERROR]: handleFrontierChartError,
  [Types.SET_GROUP]: handleSetGroup,
  [Types.FRONTIER_LOADING]: handleFrontierLoading,
  [Types.COMPARISON_ITEM_LOADING_SUCCESS]: handleComparisonItemLoadingSuccess,
  [ExperimentsTypes.ADD_COMPARISON_ITEM]: handleComparisonItemLoading,
  [Types.SET_CONSTRAINTS]: handleSetConstraints,
  [Types.REMOVE_FACTOR]: handleRemoveFactor,
  [Types.RESET_SETTINGS]: handleResetSettings,
  [Types.SET_SETTINGS]: handleSetSettings,
  [LoaderTypes.LOAD_SUCCESS]: handleLoadSuccess,
  [Types.CHART_LEGEND_FOCUS]: handleChartLegendFocus,
  [Types.TOGGLE_FRONTIER_SETTINGS]: handleToggleFrontierSettings,
  [ExperimentsTypes.SET_FRONTIER_DATE]: handleSetReloadChart,
  [Types.SET_CONSTRAINT]: handleSetConstraint,
  [Types.SET_SETTING]: handleSetSetting,
  [Types.SET_FACTORS]: handleSetFactors,
  [Types.REMOVE_ACTIVATED_FACTOR]: handleRemoveActivatedFactor,
})

function* frontierChart() {
  const { account, portfolioShortId, modelId, forecast: rawForecast, viewType, base, constraints, frontierDate } = yield select(state => ({
    account: state.global.selected.account,
    portfolioShortId: state.global.selected.portfolio,
    modelId: getSelectedModelId(state),
    forecast: getForecast(state),
    constraints: getApiConstraints(state),
    base: getBasePositionSet(state),
    viewType: getViewType(state),
    frontierDate: getFrontierDate(state),
  }))

  yield put(Creators.frontierLoading())

  // Omit the horizon so the optimizer think the provided forecast is annualized even if the selected horizon is != 252 to match the scorecard values
  const forecast = omit(rawForecast, "horizon")

  try {
    const { data, errors, messages } = yield call(apiPost, `/api/${account}/p/${portfolioShortId}/m/${modelId}/optimization-scanner-chart/${frontierDate}`, {
      objective: [{}],
      constraints,
      base: viewType === VIEW_TYPES.ACTIVE ? base : null,
      forecast,
    })

    if (size(errors)) {
      yield put(notificationCreators.addApiNotification("error", null, get(first(errors), "code"), get(first(errors), "message")))
      yield put(Creators.frontierChartError())
    } else if (size(messages)) {
      for (const message of messages) {
        yield put(notificationCreators.addApiNotification("warning", null, get(message, "code"), get(message, "message")))
      }

      if (data) {
        yield put(Creators.frontierChartSuccess(data))
      } else {
        yield put(Creators.frontierChartError())
      }
    } else {
      yield put(Creators.frontierChartSuccess(data))
    }
  } catch {
    yield put(Creators.frontierChartError())
  }
}

function* handleLoadForecast({ resource }) {
  if (resource === "compareFrontierConstraintsPreferences") {
    const { selectedCustomForecast, storedForecastId, settings } = yield select(state => ({
      selectedCustomForecast: getSelectedCustomForecast(state),
      storedForecastId: getForecastConstraint(state),
      settings: getCurrentSettings(state),
    }))

    // If forecast constraint isn't in forecasts, show notification and update user preference
    if (storedForecastId && !selectedCustomForecast) {
      yield put(notificationCreators.add("info", null, "The previously selected forecast could not be loaded."))
      yield put(Creators.setSettings(omit(settings, "forecast")))
    }
  }
}

export function* saga() {
  yield takeLatest(Types.FRONTIER_CHART, frontierChart)
  yield takeLatest(LoaderTypes.LOAD_SUCCESS, handleLoadForecast)
}
